// @ts-check

import { useCallback, useMemo } from "react";
import { findIndex } from "lodash";
import { ENTITY_TYPES as ENTITY_TYPE_CONSTANTS } from "src/consts/entityTypes";
import Consts from "../consts";
import { useUserPreferences } from "./useUserPreferences";

/** @type {user_prefs.PinnedItemEntityType[]} */
const ENTITY_TYPES = [
  /** @type {user_prefs.PinnedItemEntityType} */ (ENTITY_TYPE_CONSTANTS.TEAM),
  /** @type {user_prefs.PinnedItemEntityType} */ (ENTITY_TYPE_CONSTANTS.PERSON),
  /** @type {user_prefs.PinnedItemEntityType} */ (ENTITY_TYPE_CONSTANTS.TAG),
];

/**
 * GraphQL / UI sometimes use numeric ids; prefs and comparisons need stable strings.
 * @param {user_prefs.PinnedItem} item
 * @returns {user_prefs.PinnedItem}
 */
const normalizePinnedItem = (item) => {
  const raw = item.id;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return { ...item, id: String(raw) };
  }
  if (typeof raw === "string") {
    return item;
  }
  return item;
};

/**
 * @param {user_prefs.PinnedItem} item
 * @returns {boolean}
 */
const validateItem = (item) => {
  const { id } = item;
  const idOk =
    (typeof id === "string" && id.length > 0) ||
    (typeof id === "number" && Number.isFinite(id));
  return idOk && Object.values(ENTITY_TYPES).includes(item.entityType);
};

/**
 * Custom hook for managing pinned items in user preferences.
 */
export const useUserPinnedItems = () => {
  const {
    mergedPreferences,
    workspaceUserPreferences,
    updatePreferencesForWorkspaceUser,
  } = useUserPreferences();

  const updatePinnedItems = useCallback(
    /**
     * @param {user_prefs.PinnedItems} newPinnedItems
     * @returns {Promise<boolean>}
     */
    async (newPinnedItems) => {
      const normalized = newPinnedItems.map(normalizePinnedItem);
      for (const item of normalized) {
        if (!validateItem(item)) {
          return false;
        }
      }

      return updatePreferencesForWorkspaceUser({
        ...workspaceUserPreferences,
        pinnedItems: normalized,
      });
    },
    [workspaceUserPreferences, updatePreferencesForWorkspaceUser]
  );

  const addPinnedItem = useCallback(
    /**
     * @param {user_prefs.PinnedItem} item
     * @returns {Promise<boolean>}
     */
    async (item) => {
      const pinnedItems = mergedPreferences?.pinnedItems || [];

      const newPinnedItems = [...pinnedItems, normalizePinnedItem(item)];

      return updatePinnedItems(newPinnedItems);
    },
    [mergedPreferences?.pinnedItems, updatePinnedItems]
  );

  const removePinnedItem = useCallback(
    /**
     * @param {user_prefs.PinnedItem} item
     * @returns {Promise<boolean>}
     */
    async (item) => {
      const pinnedItems = mergedPreferences?.pinnedItems || [];

      const normalized = normalizePinnedItem(item);
      const removeIndex = findIndex(
        pinnedItems,
        (x) =>
          x.entityType === normalized.entityType &&
          String(x.id) === String(normalized.id)
      );

      if (removeIndex === -1) {
        return false;
      }

      const newPinnedItems = [...pinnedItems];
      newPinnedItems.splice(removeIndex, 1);

      return updatePinnedItems(newPinnedItems);
    },
    [mergedPreferences?.pinnedItems, updatePinnedItems]
  );

  const togglePinnedItem = useCallback(
    /**
     * @param {user_prefs.PinnedItem} item
     * @returns {Promise<boolean>}
     */
    async (item) => {
      const pinnedItems = mergedPreferences?.pinnedItems || [];
      const normalized = normalizePinnedItem(item);

      const removeIndex = findIndex(
        pinnedItems,
        (x) =>
          x.entityType === normalized.entityType &&
          String(x.id) === String(normalized.id)
      );

      if (removeIndex === -1) {
        return addPinnedItem(normalized);
      }
      return removePinnedItem(normalized);
    },
    [addPinnedItem, mergedPreferences?.pinnedItems, removePinnedItem]
  );

  const pinnedItems = useMemo(
    () =>
      (
        mergedPreferences?.pinnedItems || Consts.defaultPreferences.pinnedItems
      ).map(normalizePinnedItem),
    [mergedPreferences?.pinnedItems]
  );

  return {
    pinnedItems,
    updatePinnedItems,
    addPinnedItem,
    removePinnedItem,
    togglePinnedItem,
  };
};
